import * as React from "react";
import { TriangleAlert } from "lucide-react";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { ConfirmContext, type Confirm, type ConfirmOptions } from "./confirm-context";

/**
 * The dialog behind `useConfirm`. The context and both hooks live in
 * `confirm-context.ts`, and the split is deliberate - see the header there.
 *
 * **This file must export components and nothing else.** Adding a hook, a
 * constant or a type back into it makes it a mixed module again, which is what
 * produced `useConfirm must be used inside <ConfirmProvider>` on a tree that
 * plainly had one.
 */

interface Pending {
  options: ConfirmOptions;
  resolve: (answer: boolean) => void;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = React.useState<Pending | null>(null);

  const confirm = React.useCallback<Confirm>(
    (options) => new Promise<boolean>((resolve) => setPending({ options, resolve })),
    []
  );

  /**
   * Settle the promise as the dialog closes, never after a delay.
   *
   * The `setPending` updater is where the resolve happens so a double-press
   * cannot settle it twice: React hands the updater the current value, so the
   * second call sees `null` and does nothing. Resolving in a plain handler and
   * clearing state separately would let a fast Enter-Enter answer the same
   * question twice, and the second answer would arrive at a caller that has
   * already moved on.
   */
  const settle = React.useCallback((answer: boolean) => {
    setPending((current) => {
      current?.resolve(answer);
      return null;
    });
  }, []);

  const options = pending?.options;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}

      <Modal
        open={pending !== null}
        onClose={() => settle(false)}
        title={options?.title ?? ""}
        className="confirm"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => settle(false)}>
              {options?.cancelLabel ?? "Cancel"}
            </Button>
            <Button
              variant={options?.danger ? "danger" : "primary"}
              size="sm"
              // The dialog is a decision, so the decision is what focus lands
              // on. Escape and Cancel are both still one keystroke away.
              autoFocus
              onClick={() => settle(true)}
            >
              {options?.confirmLabel ?? "Confirm"}
            </Button>
          </>
        }
      >
        <div className="confirm__body">
          <span
            className={options?.danger ? "confirm__icon confirm__icon--danger" : "confirm__icon"}
          >
            <TriangleAlert />
          </span>
          {options?.message && <div className="confirm__message">{options.message}</div>}
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
}
